import './style/TestCard.css';
import { useNavigate } from 'react-router-dom';
import Clock from '../images/clock.png';
import { useEffect, useState } from 'react';
import { authFetch } from '../authFetch';

function TestCard(props) {
    const navigate = useNavigate();
    const [attempts, setAttempts] = useState([]);
    const [questionsAnswered, setQuestionsAnswered] = useState(null);
    const [started, setStarted] = useState(false);
    const [attemptsLoading, setAttemptsLoading] = useState(true);
    const [questionsAnsweredLoading, setQuestionsAnsweredLoading] = useState(true);
    const [selectedAttempt, setSelectedAttempt] = useState(null);
    const [timerEnabled, setTimerEnabled] = useState(true);
    const [timed, setTimed] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const deleteTest = async () => {
        setDeleting(true);
        try {
            const response = await authFetch(`${process.env.REACT_APP_API_BASE}/testCard/deleteTest`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ testId: props.test.test_id }),
            });
            const data = await response.json();
            if (data.success && props.onDelete) { props.onDelete(props.test.test_id); }
            else { setDeleting(false); }
        }
        catch (error) { console.error('Error deleting test:', error); setDeleting(false); }
    };
 
    const handleStart = () => {
        const attemptNumber = selectedAttempt === null ? attempts.length + 1 : selectedAttempt;
        const selectedAttemptData = attempts.find(a => a.attempt_number === attemptNumber);
        if (selectedAttemptData && selectedAttemptData.completed) {
            navigate(`/tests/${props.test.uuid}/review`, { 
                state: { attemptNumber, testName: props.test.uuid, testId: props.test.test_id } 
            });
        }
        else {
            const isTimed = selectedAttemptData ? selectedAttemptData.timed : timerEnabled;
            navigate(`/tests/${props.test.uuid}`, { 
                state: { attemptNumber, testName: props.test.uuid, testId: props.test.test_id, timed: isTimed } 
            });
        }
    };

    const getAttempts = async () => {
        const response = await authFetch(`${process.env.REACT_APP_API_BASE}/testCard/getAttempts/${props.test.test_id}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            }
        });
        const data = await response.json();
        if (data.success && data.attempts) {
            const timedIncomplete = data.attempts.find(a => a.started && a.timed && !a.completed);
            if (timedIncomplete) {
                await authFetch(`${process.env.REACT_APP_API_BASE}/test/submitTest`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ testId: props.test.test_id, attemptNumber: timedIncomplete.attempt_number })
                });
                return getAttempts();
            }
            setAttempts(data.attempts);
            setSelectedAttempt(data.attempts.length > 0 ? data.attempts[0].attempt_number : null);
            setStarted(data.attempts.length > 0 ? data.attempts[0].started : false);
            if (data.attempts.length > 0 && data.attempts[0].started) {setTimerEnabled(data.attempts[0].timed);}
            setAttemptsLoading(false);
            if (data.attempts.length > 0) {getQuestionsAnswered(data.attempts[0].attempt_number);} 
            else {
                setQuestionsAnswered(0);
                setQuestionsAnsweredLoading(false);
            }
        } 
    }

    const getQuestionsAnswered = async (attemptNumber) => {
        const response = await authFetch(`${process.env.REACT_APP_API_BASE}/testCard/getQuestionsAnswered/${props.test.test_id}/${attemptNumber}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            }
        });
        const data = await response.json();
        if (data.questionsAnswered !== null) {
            setQuestionsAnswered(data.questionsAnswered);
            setQuestionsAnsweredLoading(false);
        }
    }

    const handleAttemptChange = (e) => {
        const attemptValue = e.target.value === '' ? null : parseInt(e.target.value);
        setSelectedAttempt(attemptValue);
        if (attemptValue !== null) {
            const selectedAttemptData = attempts.find(a => a.attempt_number === attemptValue);
            setStarted(selectedAttemptData.started);
            setTimed(selectedAttemptData.timed);
            if (selectedAttemptData.started) {setTimerEnabled(selectedAttemptData.timed);}
            getQuestionsAnswered(attemptValue);
        } 
        else {
            setQuestionsAnswered(0);
            setStarted(false);
            setTimed(false);
        }
    }

    useEffect(() => {
        getAttempts();
    }, []);

    return (
        <>
            {!(attemptsLoading || questionsAnsweredLoading) && !deleting && <div className="test-card">
                <div className="card-header">
                    <div className="card-tags">
                        <div className="card-module module-tag">
                            <p>Module {props.test.module}</p>
                        </div>
                        {props.test.difficulty && (
                            <div className={`card-module${props.test.difficulty === 'Easy' ? ' easy' : ' hard'}`}>
                                <p>{props.test.difficulty}</p>
                            </div>
                        )}
                    </div>
                    <div className="card-attempts">
                        <select value={selectedAttempt || ''} onChange={handleAttemptChange}>
                            {(attempts.length === 0 || attempts[0]?.completed) && (
                                <option key="new" value="">New Attempt</option>
                            )}
                            {attempts && attempts.map((attempt) => (
                                <option key={attempt.attempt_number} value={attempt.attempt_number}>
                                    Attempt {attempt.attempt_number}
                                </option>
                            ))}
                        </select>
                        <span className="delete-test" onClick={deleteTest}>&#128465;</span>
                    </div>
                </div>
                <div className="card-body">
                    <div className="card-title">
                        <h2>Test #{props.test.uuid}</h2>
                    </div>
                    <div className="card-action">
                        <div className="card-button">
                            <button onClick={handleStart} disabled={started && timed && !attempts.find(a => a.attempt_number === selectedAttempt)?.completed}>
                                {attempts && selectedAttempt && attempts.find(a => a.attempt_number === selectedAttempt)?.completed
                                    ? 'Review'
                                    : started && timed
                                    ? 'Locked'
                                    : !started
                                    ? 'Start'
                                    : 'Continue'
                                }
                            </button>
                        </div>
                        <div className={`card-timer${started ? ' disabled' : ''}`} onClick={() => { if (!started) setTimerEnabled(prev => !prev); }}>
                            <img src={Clock} alt="Timer" />
                            <span>Timer {timerEnabled ? 'On' : 'Off'}</span>
                        </div>
                    </div>
                </div>
                <div className="card-progress">
                    {selectedAttempt && attempts.find(a => a.attempt_number === selectedAttempt)?.completed ? (() => {
                        const score = attempts.find(a => a.attempt_number === selectedAttempt).score;
                        const ratio = Math.min(Math.max((score - 200) / 600, 0), 1);
                        const r = Math.round(220 - ratio * 180);
                        const g = Math.round(60 + ratio * 127);
                        const b = Math.round(60);
                        return (
                            <>
                                <div className="progress-bar-track">
                                    <div className="progress-bar-fill" style={{ width: '100%', backgroundColor: `rgb(${r}, ${g}, ${b})` }}></div>
                                </div>
                                <div className="questions-answered">
                                    <p>Score: {score} / 800</p>
                                </div>
                            </>
                        );
                    })() : (
                        <>
                            <div className="progress-bar-track">
                                <div className="progress-bar-fill" style={{ width: `${(questionsAnswered / 22) * 100}%` }}></div>
                            </div>
                            <div className="questions-answered">
                                <p>{questionsAnswered} of 22 answered</p>
                            </div>
                        </>
                    )}
                </div>
            </div>}
            {(attemptsLoading || questionsAnsweredLoading || deleting) && <div className="test-card-loading">
                <div className="shimmer"></div>
            </div>}
        </>
    );
}

export default TestCard;